/** @format */

import {useRef, useEffect, useState, useMemo} from 'react';

import {Box, Typography} from '@mui/material';
import {styled} from '@mui/material/styles';

import {Chart, registerables} from 'chart.js';
import type {ChartConfiguration} from 'chart.js';

import theme from 'theme';

Chart.register(...registerables);

// Chart.js is only used in chat (dark theme) — set defaults once at module level
const chartTextColor = theme.palette.semantic_dark?.surface.onSurfaceLight ?? '';
const chartGridColor = theme.palette.semantic_dark?.surface.surface3 ?? '';
Chart.defaults.color = chartTextColor;
Chart.defaults.plugins.legend.labels.color = chartTextColor;
Chart.defaults.plugins.title.color = chartTextColor;
Chart.defaults.scale.grid.color = chartGridColor;
Chart.defaults.scale.ticks.color = chartTextColor;

// Palette for datasets that arrive without explicit colors. Order chosen for max contrast.
export const DATASET_COLOR_PALETTE = [
  theme.palette.semantic_dark?.primary.main ?? '#3E88C5',
  theme.palette.semantic_dark?.secondary.main ?? '#419466',
  theme.palette.semantic_dark?.tertiary.main ?? '#B17629',
  theme.palette.semantic_dark?.bad.main ?? '#C06868',
] as const;

const ChartContainer = styled(Box)(({theme}) => ({
  maxWidth: '600px',
  width: '100%',
  padding: theme.spacing(2),
}));

const ErrorText = styled(Typography)(({theme}) => ({
  color: theme.palette.semantic_dark?.bad.main,
  fontSize: '14px',
}));

interface ChartBlockProps {
  config: ChartConfiguration;
}

// AI responses may include multi-axis charts with all axes on the left by default.
// Axes can be defined explicitly in scales, or implicitly via yAxisID in datasets.
export const distributeYAxes = (config: ChartConfiguration): ChartConfiguration => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- AI may send invalid config without data
  if (!config.data) return config;

  const datasets = ('datasets' in config.data ? config.data.datasets : []) as Array<{
    yAxisID?: string;
  }>;
  const existingScales = config.options?.scales ?? {};

  // Collect all y-axis IDs: from explicit scales + from dataset yAxisID references
  const axisIds = new Set<string>();
  for (const key of Object.keys(existingScales)) {
    if (key === 'y' || /^y\d+$/.test(key)) axisIds.add(key);
  }
  for (const ds of datasets) {
    if (ds.yAxisID) axisIds.add(ds.yAxisID);
  }

  if (axisIds.size <= 1) return config;

  const yAxes = [...axisIds];
  const patchedScales = {...existingScales};
  yAxes.forEach((key, index) => {
    const scale = patchedScales[key] ?? {};
    patchedScales[key] = {
      ...(typeof scale === 'object' ? scale : {}),
      position: index === 0 ? 'left' : 'right',
      ...(index > 0 && {
        grid: {...(typeof scale === 'object' ? scale.grid : {}), drawOnChartArea: false},
      }),
    };
  });

  return {
    ...config,
    options: {...config.options, scales: patchedScales},
  };
};

// AI may omit borderColor/backgroundColor on datasets. Without defaults Chart.js falls back to
// Chart.defaults.borderColor which renders datasets nearly invisible against the grid. Assign a
// distinct palette color per dataset so charts stay readable.
export const applyDatasetColors = (config: ChartConfiguration): ChartConfiguration => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- AI may send invalid config without data
  if (!config.data || !('datasets' in config.data)) return config;

  const datasets = config.data.datasets as Array<{
    borderColor?: unknown;
    backgroundColor?: unknown;
  }>;
  let mutated = false;

  const patched = datasets.map((ds, index) => {
    const hasBorder = ds.borderColor != null;
    const hasBg = ds.backgroundColor != null;
    if (hasBorder && hasBg) return ds;

    mutated = true;
    const fallback = DATASET_COLOR_PALETTE[index % DATASET_COLOR_PALETTE.length];
    return {
      ...ds,
      ...(hasBorder ? {} : {borderColor: fallback}),
      ...(hasBg ? {} : {backgroundColor: fallback}),
    };
  });

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- mutated set inside .map callback, TS narrowing misses this
  if (!mutated) return config;

  return {
    ...config,
    data: {...config.data, datasets: patched},
  } as ChartConfiguration;
};

const ChartBlock = ({config}: ChartBlockProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);
  const [error, setError] = useState<string | null>(null);

  const adjustedConfig = useMemo(() => applyDatasetColors(distributeYAxes(config)), [config]);

  useEffect(() => {
    if (!canvasRef.current) return;

    try {
      if (chartRef.current) {
        chartRef.current.destroy();
      }

      chartRef.current = new Chart(canvasRef.current, adjustedConfig);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Chart rendering error');
    }

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [adjustedConfig]);

  if (error) {
    return <ErrorText>{error}</ErrorText>;
  }

  return (
    <ChartContainer>
      <canvas ref={canvasRef} />
    </ChartContainer>
  );
};

export default ChartBlock;
